<script>
  import { onMount } from 'svelte';

  let invoices = [];
  let loading = true;
  let error = null;
  let teacherName = '';

  // Fetch teacher invoices on component mount
  onMount(() => {
    fetchTeacherInvoices();
  });

  async function fetchTeacherInvoices() {
    loading = true;
    error = null;

    const token = localStorage.getItem('jwtToken');
          if (!token) {
        error = 'JWT žetonas nerastas. Prašome prisijungti.';
        loading = false;
        return;
      }

    try {
      const response = await fetch('/studentplayground/api/teacher-invoices/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        invoices = data.invoices;
        teacherName = data.teacher_name;
      } else {
        const errorData = await response.json();
        error = errorData.message || 'Nepavyko gauti sąskaitų faktūrų.';
      }
    } catch (err) {
      error = `Tinklo klaida: ${err.message}`;
    } finally {
      loading = false;
    }
  }

  // Download invoice PDF
  function downloadInvoice(invoice) {
    if (invoice.pdf_url) {
      // Create a link to download the file
      const link = document.createElement('a');
      link.href = invoice.pdf_url;
      link.download = invoice.file_name;
      link.target = '_blank'; // Open in new tab as backup
      link.click();
    }
  }

  // Format date for display
  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Translate month display to Lithuanian
  function translateMonthDisplay(monthDisplay) {
    if (!monthDisplay) return monthDisplay;
    
    const monthTranslations = {
      'January': 'Sausis',
      'February': 'Vasaris', 
      'March': 'Kovas',
      'April': 'Balandis',
      'May': 'Gegužė',
      'June': 'Birželis',
      'July': 'Liepa',
      'August': 'Rugpjūtis',
      'September': 'Rugsėjis',
      'October': 'Spalis',
      'November': 'Lapkritis',
      'December': 'Gruodis'
    };
    
    // Replace English month names with Lithuanian ones
    let translatedMonth = monthDisplay;
    for (const [english, lithuanian] of Object.entries(monthTranslations)) {
      translatedMonth = translatedMonth.replace(english, lithuanian);
    }
    
    return translatedMonth;
  }
</script>

<div class="teacher-invoices-container">
  <div class="header-section">
    <h2>Mano sąskaitos faktūros</h2>
    {#if teacherName}
      <p class="teacher-welcome">Sveiki, {teacherName}</p>
    {/if}
  </div>

  <!-- Error message -->
  {#if error}
    <div class="alert alert-error">
      <strong>Klaida:</strong> {error}
    </div>
  {/if}

  <!-- Loading state -->
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Kraunamos jūsų sąskaitos faktūros...</p>
    </div>
  {:else if invoices.length === 0}
    <!-- No invoices found -->
    <div class="no-invoices">
      <div class="no-invoices-icon">📄</div>
      <h3>Sąskaitų faktūrų nerasta</h3>
      <p>Jūs dar neturite išsaugotų sąskaitų faktūrų. Jos atsiras čia, kai jas sukurs administratorius.</p>
    </div>
  {:else}
    <!-- Invoices list -->
    <div class="invoices-section">
      <h3>Jūsų išsaugotos sąskaitos faktūros ({invoices.length})</h3>
      
      <div class="invoices-grid">
        {#each invoices as invoice}
          <div class="invoice-card">
                         <div class="invoice-header">
               <div class="invoice-number">
                 <span class="number-display">{invoice.invoice_number || 'N/A'}</span>
               </div>
               <div class="invoice-month">
                 <span class="month-display">{translateMonthDisplay(invoice.month_display)}</span>
                 <span class="month-code">({invoice.month})</span>
               </div>
               <div class="invoice-date">
                 <small>Sukurta: {formatDate(invoice.created_at)}</small>
               </div>
             </div>
            
            <div class="invoice-actions">
              <button 
                class="btn btn-primary" 
                on:click={() => downloadInvoice(invoice)}
                title="Atsisiųsti PDF"
              >
                📄 Atsisiųsti PDF
              </button>
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Refresh button -->
  <div class="refresh-section">
    <button 
      class="btn btn-secondary" 
      on:click={fetchTeacherInvoices}
      disabled={loading}
    >
      {loading ? 'Atnaujinama...' : '🔄 Atnaujinti sąskaitas faktūras'}
    </button>
  </div>
</div>

<style>
  .teacher-invoices-container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
    background-color: #f8f9fa;
    min-height: 100vh;
  }

  .header-section {
    text-align: center;
    margin-bottom: 30px;
    padding: 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .header-section h2 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 2em;
  }

  .teacher-welcome {
    margin: 0;
    color: #666;
    font-size: 1.1em;
  }

  .alert {
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 20px;
  }

  .alert-error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
  }

  .loading-container {
    text-align: center;
    padding: 60px 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #fab45a;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .no-invoices {
    text-align: center;
    padding: 60px 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .no-invoices-icon {
    font-size: 4em;
    margin-bottom: 20px;
    opacity: 0.5;
  }

  .no-invoices h3 {
    color: #666;
    margin-bottom: 15px;
  }

  .no-invoices p {
    color: #888;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.5;
  }

  .invoices-section {
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 20px;
  }

  .invoices-section h3 {
    margin: 0 0 20px 0;
    color: #333;
    border-bottom: 2px solid #fab45a;
    padding-bottom: 10px;
  }

  .invoices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }

  .invoice-card {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
    background-color: #fafafa;
    transition: box-shadow 0.2s ease;
  }

  .invoice-card:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  .invoice-header {
    margin-bottom: 15px;
  }

  .invoice-number {
    margin-bottom: 10px;
    text-align: center;
  }

  .number-display {
    font-size: 1.3em;
    font-weight: bold;
    color: #fab45a;
    background-color: #f8f9fa;
    padding: 5px 12px;
    border-radius: 20px;
    border: 2px solid #fab45a;
  }

  .invoice-month {
    margin-bottom: 8px;
  }

  .month-display {
    font-size: 1.2em;
    font-weight: bold;
    color: #333;
  }

  .month-code {
    color: #666;
    font-size: 0.9em;
    margin-left: 5px;
  }

  .invoice-date small {
    color: #888;
    font-size: 0.85em;
  }

  .invoice-actions {
    display: flex;
    justify-content: center;
  }

  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background-color: #fab45a;
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #e09346;
    transform: translateY(-1px);
  }

  .btn-secondary {
    background-color: #6c757d;
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #5a6268;
    transform: translateY(-1px);
  }

  .refresh-section {
    text-align: center;
    padding: 20px;
  }

  /* Mobile responsiveness */
  @media (max-width: 600px) {
    .teacher-invoices-container {
      padding: 12px;
    }

    .header-section {
      padding: 15px;
    }

    .header-section h2 {
      font-size: 1.5em;
    }

    .invoices-grid {
      grid-template-columns: 1fr;
    }

    .invoice-card {
      padding: 15px;
    }

    .btn {
      width: 100%;
      padding: 12px;
    }
  }
</style> 